import { useCustom } from "@refinedev/core";

import { OntwerpRenderer } from "./OntwerpRenderer";
import type { Ontwerp, Rij } from "./ontwerpTypes";
import "./schermbouwer.css";

/**
 * Gepubliceerde tegels uit de schermbouwer, op het dashboard.
 *
 * <p>Dit is de stap die "Publiceren" betekenis geeft. Het dashboard kent zijn tegels bij code en
 * gooit een code die het niet kent eruit -- precies het patroon waardoor op het relatiescherm een
 * nieuwe tegel stil verdween. Zonder deze haak zou een gebruiker een tegel ontwerpen, op publiceren
 * drukken, een bevestiging krijgen en daarna nergens iets zien.
 *
 * <p>De tegel toont de <b>gepubliceerde</b> versie, niet de laatste. Wie een gepubliceerde tegel
 * aanpast maakt een concept; dat hoort niet meteen bij iedereen op het scherm te staan.
 *
 * <p>De gegevens komen per kijker van de server, met diens eigen rechten en vestigingen. Twee
 * collega's zien dus dezelfde tegel met andere regels erin, en dat is de bedoeling.
 */

export interface AiTegel {
  tegelCode: string;
  naam: string;
  schermtype: string;
  versie: number;
  configuratie: Ontwerp;
}

/** De gepubliceerde tegels; leeg zolang er niets gepubliceerd is of het laden nog loopt. */
export function useAiTegels(): AiTegel[] {
  const { data } = useCustom<AiTegel[]>({ url: "schermbouwer/gepubliceerd", method: "get" });
  return Array.isArray(data?.data) ? data.data : [];
}

export function AiTegelInhoud({ tegel }: { tegel: AiTegel }) {
  const { data, isLoading } = useCustom<{ rijen: Rij[]; aggregaten: Record<string, number> }>({
    url: `schermbouwer/gepubliceerd/${encodeURIComponent(tegel.tegelCode)}/gegevens`,
    method: "get",
  });

  if (isLoading) {
    return <p className="sb__waarschuwing">…</p>;
  }
  return (
    <OntwerpRenderer
      ontwerp={tegel.configuratie}
      rijen={data?.data?.rijen ?? []}
      aggregaten={data?.data?.aggregaten ?? {}}
      alleenTonen
    />
  );
}
